import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Check, Loader2, Save, Plus, Pencil, Trash2, Copy } from "lucide-react";
import { api } from "@/lib/api";
import { Alert, Button, Card, DataField, FormField, PageContainer, PageHeader, Textarea } from "@/components/ui";
import { TitularPicker } from "@/components/TitularPicker";
import { ParteBloqueada, TramitadorAutorizadoField } from "@/components/TramitadorAutorizadoField";
import { SectorNoHabilitadoPanel } from "@/components/SectorNoHabilitadoPanel";
import { PaisesPicker } from "@/components/PaisesPicker";
import { useTelecomAccess } from "@/hooks/useTelecomAccess";
import {
  CapturaDocumentos,
  subirDocumentosCapturados,
  type DocumentoCapturado,
} from "@/components/CapturaDocumentos";
import {
  NormaModal,
  nuevaNormaForm,
  normaFormToPayload,
  type Norma,
  type NormaForm,
} from "@/components/telecom/NormaModal";

// ============================================================================
// Tipos
// ============================================================================

interface RegistroResumen { id: string; razonSocial: string; rfc: string; estatus: string; }

/**
 * Validación automática de duplicidad sobre el «Modelo principal».
 * Refleja DuplicidadModeloResultado del backend. Las coincidencias de otro
 * titular llegan anonimizadas (folio/estado en null) por confidencialidad.
 */
type DuplicidadDecision =
  | "PERMITIDO"
  | "ADVERTENCIA_RECHAZO"
  | "BLOQUEADO_ACEPTADO"
  | "BLOQUEADO_EN_PROCESO";
interface DuplicidadCoincidencia {
  folio: string | null;
  estadoCodigo: string | null;
  estadoNombre: string | null;
  servicioCodigo: string | null;
  fechaCreacion: string | null;
  mismoTitular: boolean;
}
interface DuplicidadResultado {
  decision: DuplicidadDecision;
  mensaje: string | null;
  coincidencias: DuplicidadCoincidencia[];
  sugerirEvidencia: boolean;
}
const duplicidadBloquea = (d?: DuplicidadResultado) =>
  d?.decision === "BLOQUEADO_ACEPTADO" || d?.decision === "BLOQUEADO_EN_PROCESO";
interface Servicio { codigo: string; nombre: string; descripcion: string; }
interface Esquema { codigo: string; nombre: string; descripcion: string; }
interface TipoProducto { codigo: string; nombre: string; }
interface Pais { id: string; codigo: string; nombre: string; }
interface Sepomex { cp: string; estado: string; municipio: string; asentamiento: string; }
interface ContextoMio {
  titularId: string;
  titularRazonSocial: string;
  tramitadorId: string | null;
  tramitadorRazonSocial: string | null;
  titularDomicilio: {
    calle: string | null;
    numeroExterior: string | null;
    numeroInterior: string | null;
    colonia: string | null;
    municipio: string | null;
    entidadFederativa: string | null;
    codigoPostal: string | null;
    pais: string | null;
  } | null;
  titularPuedeActuarComoTitular: boolean;
  titularPuedeActuarComoTramitador: boolean;
}

const CERTIFICADO_UNICO = "CERTIFICADO_UNICO";
const MUESTREO = "MUESTREO_RECOLECCION";
const OTRO = "OTRO_TELECOM";
const TELEFONO_RX = /^[\d\s()-]{7,20}$/;

// Pasos del wizard de captura (reduce el scroll: una sección a la vez).
const PASOS_TITULOS = ["Solicitante", "Servicio", "Producto", "Bodega", "Normas", "Revisar y enviar"];
const PASO_DOCS = 5; // último paso: comentarios + documentos + envío
const PASO_TOTAL = PASOS_TITULOS.length;

// ============================================================================
// Página principal
// ============================================================================

export function SolicitudTelecomAltaPage() {
  const access = useTelecomAccess();
  if (access.loading) {
    return (
      <div className="flex items-center justify-center p-12 text-sm text-fg-subtle">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Validando permisos…
      </div>
    );
  }
  if (!access.allowed) {
    return (
      <SectorNoHabilitadoPanel
        sectorNombre="Telecomunicaciones"
        sectorCodigo="TELECOM"
        titularRazonSocial={access.titularRazonSocial}
        sinContexto={access.sinContexto}
      />
    );
  }
  return <SolicitudTelecomAltaForm />;
}

function SolicitudTelecomAltaForm() {
  const navigate = useNavigate();
  const access = useTelecomAccess();
  const [params] = useSearchParams();
  const titularIdInicial =
    params.get("titularId") ?? window.sessionStorage.getItem("sicen.contexto.titularId");

  // -- Estado del formulario --
  const [titular, setTitular] = useState<RegistroResumen | null>(null);
  const [tramitador, setTramitador] = useState<RegistroResumen | null>(null);
  // Cuántos tramitadores autorizados (carta poder vigente) tiene el titular
  // seleccionado; con >1 la selección restringida se vuelve obligatoria.
  const [numTramitadoresAut, setNumTramitadoresAut] = useState(0);
  // Esta pantalla es exclusiva del alta de "Certificado único" (Form Maestro).
  // Los demás trámites se inician desde el menú de servicios (pantallas
  // dedicadas), conforme al diseño funcional v7.
  const [servicio, setServicio] = useState(CERTIFICADO_UNICO);
  const [otroServicio, setOtroServicio] = useState("");
  const [esquema, setEsquema] = useState("");
  const [tipoProducto, setTipoProducto] = useState("");
  const [productoNombre, setProductoNombre] = useState("");
  const [productoModelo, setProductoModelo] = useState("");
  const [marca, setMarca] = useState("");
  const [productoAccesorios, setProductoAccesorios] = useState("");
  const [productoFabricante, setProductoFabricante] = useState("");
  const [productoFraccion, setProductoFraccion] = useState("");
  const [productoModelos, setProductoModelos] = useState("");
  const [productoCantidadLote, setProductoCantidadLote] = useState<string>("");
  const [productoCantidadSerie, setProductoCantidadSerie] = useState<string>("");
  const [paisesOrigen, setPaisesOrigen] = useState<string[]>([]);
  const [paisesProcedencia, setPaisesProcedencia] = useState<string[]>([]);
  const [certificadoAnterior, setCertificadoAnterior] = useState("");
  const [bajoProtesta, setBajoProtesta] = useState(false);
  const [numFamilia, setNumFamilia] = useState("");
  const [comentario, setComentario] = useState("");
  const [normas, setNormas] = useState<NormaForm[]>([]);
  const [modalNorma, setModalNorma] = useState<NormaForm | null>(null);
  const [docs, setDocs] = useState<DocumentoCapturado[]>([]);

  // Paso actual del wizard (captura por pasos para reducir el scroll).
  const [step, setStep] = useState(0);

  // Envío en curso + solicitud / expediente creados (para reintento sin re-crear).
  const [enviando, setEnviando] = useState(false);
  const [solicitudCreada, setSolicitudCreada] = useState<string | null>(null);
  const [expedienteCreado, setExpedienteCreado] = useState<string | null>(null);

  // Bodega
  const [bodMismoDom, setBodMismoDom] = useState(false);
  const [bodCalle, setBodCalle] = useState("");
  const [bodNumExt, setBodNumExt] = useState("");
  const [bodNumInt, setBodNumInt] = useState("");
  const [bodCP, setBodCP] = useState("");
  const [bodColonia, setBodColonia] = useState("");
  const [bodMunicipio, setBodMunicipio] = useState("");
  const [bodEntidad, setBodEntidad] = useState("");
  const [bodNombreResp, setBodNombreResp] = useState("");
  const [bodTelefonoResp, setBodTelefonoResp] = useState("");
  const [coloniasSepomex, setColoniasSepomex] = useState<string[]>([]);

  const [error, setError] = useState<string | null>(null);

  // -- Cargas de catálogos --
  const qServicios = useQuery({
    queryKey: ["telecom-servicios"],
    queryFn: async () => (await api.get<Servicio[]>("/api/sector/telecom/servicios")).data,
  });
  const qEsquemas = useQuery({
    queryKey: ["telecom-esquemas"],
    queryFn: async () => (await api.get<Esquema[]>("/api/sector/telecom/esquemas")).data,
  });
  const qTiposProducto = useQuery({
    queryKey: ["telecom-tipos-producto", esquema],
    queryFn: async () =>
      (await api.get<TipoProducto[]>(
        `/api/sector/telecom/tipos-producto${esquema ? `?esquema=${esquema}` : ""}`,
      )).data,
    enabled: servicio === CERTIFICADO_UNICO && !!esquema,
  });
  const qPaises = useQuery({
    queryKey: ["telecom-paises"],
    queryFn: async () => (await api.get<Pais[]>("/api/sector/telecom/paises")).data,
    enabled: servicio === CERTIFICADO_UNICO,
  });
  const qNormas = useQuery({
    queryKey: ["telecom-normas"],
    queryFn: async () => (await api.get<Norma[]>("/api/sector/telecom/normas")).data,
    enabled: servicio === CERTIFICADO_UNICO,
  });

  // --------------------------------------------------------------------------
  // Duplicidad del «Modelo principal»
  // Se consulta mientras se captura (con debounce) para avisar antes de enviar
  // el formulario. La barrera real está en el backend, que vuelve a validar en
  // el alta: esto es solo para no hacer llenar el formulario en vano.
  // --------------------------------------------------------------------------
  const [modeloDebounced, setModeloDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setModeloDebounced(productoModelo.trim()), 400);
    return () => clearTimeout(t);
  }, [productoModelo]);

  const qDuplicidad = useQuery({
    queryKey: ["telecom-duplicidad-modelo", modeloDebounced, titular?.id],
    queryFn: async () =>
      (await api.get<DuplicidadResultado>(
        "/api/sector/telecom/solicitudes/verificar-duplicidad",
        { params: { modelo: modeloDebounced, titularId: titular?.id } },
      )).data,
    enabled: modeloDebounced.length > 0,
    // El estado de las solicitudes cambia con el flujo: no cachear de más.
    staleTime: 15_000,
  });
  const duplicidad = qDuplicidad.data;
  const modeloBloqueado = duplicidadBloquea(duplicidad);

  // Cargo todos los contextos del usuario; el actual lo seleccionamos por
  // titularIdInicial. Conservo el dataset entero porque el domicilio del
  // titular vive aquí y lo usa "mismo domicilio fiscal" en Bodega.
  const qContextos = useQuery<ContextoMio[]>({
    queryKey: ["contextos-mios"],
    queryFn: async () => (await api.get<ContextoMio[]>("/api/contextos/mios")).data,
  });
  const ctxActivo = qContextos.data?.find((c) => c.titularId === titularIdInicial) ?? null;

  // Precarga titular/tramitador a partir del contexto activo, respetando las
  // banderas del RL: si el contexto es un tramitador puro (no puede ser
  // titular), NO se auto-selecciona como titular — se pre-puebla como
  // tramitador y el usuario debe elegir un titular cliente manualmente.
  const ctxEsTramitadorPuro =
    !!ctxActivo &&
    !ctxActivo.titularPuedeActuarComoTitular &&
    ctxActivo.titularPuedeActuarComoTramitador;

  // Bloqueo de partes para usuarios externos: el contexto (RL) determina el
  // titular y/o el tramitador y no deben poder modificarse. El titular solo se
  // bloquea cuando el contexto lo fija (titular propio o carta poder); en el
  // caso tramitador-puro el titular cliente sí lo elige el usuario.
  const esExterno = !access.esInterno;
  const titularBloqueado = esExterno && !!ctxActivo?.titularPuedeActuarComoTitular;
  const tramitadorBloqueado =
    esExterno && (!!ctxActivo?.tramitadorId || ctxEsTramitadorPuro);

  useEffect(() => {
    if (!ctxActivo) return;
    if (!titular && ctxActivo.titularPuedeActuarComoTitular) {
      setTitular({ id: ctxActivo.titularId, razonSocial: ctxActivo.titularRazonSocial, rfc: "", estatus: "" });
    }
    if (!tramitador) {
      if (ctxActivo.tramitadorId && ctxActivo.tramitadorRazonSocial) {
        // Caso con carta poder: el contexto trae tramitador explícito.
        setTramitador({ id: ctxActivo.tramitadorId, razonSocial: ctxActivo.tramitadorRazonSocial, rfc: "", estatus: "" });
      } else if (ctxEsTramitadorPuro) {
        // Caso REPRESENTANTE_DIRECTO de un RL solo-tramitador: el RL del
        // contexto es el tramitador en la solicitud.
        setTramitador({ id: ctxActivo.titularId, razonSocial: ctxActivo.titularRazonSocial, rfc: "", estatus: "" });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctxActivo]);

  // Cuando cambia el servicio: limpiar campos que no aplican.
  const cambiarServicio = (codigo: string) => {
    setServicio(codigo);
    setOtroServicio("");
    if (codigo === MUESTREO) {
      setEsquema("ESQUEMA_1"); // RN: muestreo fuerza E1
    } else if (codigo !== CERTIFICADO_UNICO) {
      // Servicios "ligeros" no usan esquema, tipo, producto rico, bodega, normas
      setEsquema("");
      setTipoProducto("");
    }
  };

  // SEPOMEX al cambiar CP de bodega.
  useEffect(() => {
    if (bodMismoDom) return;
    if (!/^\d{5}$/.test(bodCP)) {
      setColoniasSepomex([]);
      return;
    }
    api.get<Sepomex[]>(`/api/sepomex?cp=${bodCP}`)
      .then(({ data }) => {
        if (data.length === 0) return;
        setBodEntidad(data[0].estado);
        setBodMunicipio(data[0].municipio);
        setColoniasSepomex(data.map((x) => x.asentamiento));
      })
      .catch(() => setColoniasSepomex([]));
  }, [bodCP, bodMismoDom]);

  // Toggle "mismo domicilio fiscal": copia el domicilio del titular que viene
  // en /api/contextos/mios. Si se desactiva, limpia los campos.
  useEffect(() => {
    if (!bodMismoDom) {
      // Al desactivar, limpia para que el usuario reedite desde cero.
      setBodCalle(""); setBodNumExt(""); setBodNumInt("");
      setBodCP(""); setBodColonia(""); setBodMunicipio(""); setBodEntidad("");
      setColoniasSepomex([]);
      return;
    }
    const dom = ctxActivo?.titularDomicilio;
    if (!dom) return;
    setBodCalle(dom.calle ?? "");
    setBodNumExt(dom.numeroExterior ?? "");
    setBodNumInt(dom.numeroInterior ?? "");
    setBodCP(dom.codigoPostal ?? "");
    setBodColonia(dom.colonia ?? "");
    setBodMunicipio(dom.municipio ?? "");
    setBodEntidad(dom.entidadFederativa ?? "");
    // colonia ya viene del titular; no necesita lookup SEPOMEX adicional.
    if (dom.colonia) setColoniasSepomex([dom.colonia]);
  }, [bodMismoDom, ctxActivo]);

  const isCU = servicio === CERTIFICADO_UNICO;

  // -- Submit --
  const construirBody = (): Record<string, unknown> => {
    const body: Record<string, unknown> = {
        titularId: titular!.id,
        tramitadorId: tramitador?.id ?? null,
        servicioCodigo: servicio,
        // RF-003: identidad comercial y técnica se guarda tal cual se capturó —
        // «iPhone» no puede volverse «IPHONE». Solo se recorta el espacio sobrante.
        otroServicio: servicio === OTRO ? otroServicio.trim() : null,
        esquemaCodigo: esquema || null,
        tipoProductoCodigo: tipoProducto || null,
        productoNombre: productoNombre.trim(),
        productoModelo: productoModelo.trim(),
        marca: marca.trim(),
        productoAccesorios: productoAccesorios.trim() || null,
        productoFabricante: productoFabricante.trim() || null,
        productoFraccionArancelaria: productoFraccion.trim() || null,
        productoModelos: productoModelos.trim() || null,
        productoCantidadLote: productoCantidadLote ? Number(productoCantidadLote) : null,
        productoCantidadSerie: productoCantidadSerie ? Number(productoCantidadSerie) : null,
        productoNumImeis: extractImeisDeNormas(normas),
        paisesOrigenIds: paisesOrigen.length ? paisesOrigen : null,
        paisesProcedenciaIds: paisesProcedencia.length ? paisesProcedencia : null,
        certificadoAnterior: !isCU ? certificadoAnterior.trim().toUpperCase() || null : null,
        bajoProtesta: !isCU ? bajoProtesta : false,
        numFamiliaGrupos: isCU ? numFamilia.trim().toUpperCase() || null : null,
        normaCodigo: normas[0]?.normaCodigo ?? "—",
        comentario: comentario.trim(),
        normas: normas.map(normaFormToPayload),
        bodega: isCU ? {
          mismoDomicilioFiscal: bodMismoDom,
          calle: bodCalle.trim().toUpperCase(),
          numeroExterior: bodNumExt.trim().toUpperCase(),
          numeroInterior: bodNumInt.trim().toUpperCase() || null,
          colonia: bodColonia,
          municipio: bodMunicipio.trim().toUpperCase(),
          entidadFederativa: bodEntidad.trim().toUpperCase(),
          codigoPostal: bodCP.trim(),
          pais: "MEX",
          nombreResponsable: bodNombreResp.trim(),
          telefonoResponsable: bodTelefonoResp.trim(),
        } : null,
    };
    return body;
  };

  const validar = (): string | null => {
    if (!titular) return "Selecciona un titular.";
    if (!tramitadorBloqueado && numTramitadoresAut > 1 && !tramitador)
      return "Selecciona el tramitador autorizado para este titular.";
    if (!servicio) return "Selecciona el tipo de trámite.";
    if (servicio === OTRO && !otroServicio.trim()) return "Captura el texto de 'Otro'.";
    if (!productoNombre.trim() || !productoModelo.trim() || !marca.trim()) return "Producto, modelo y marca son obligatorios.";
    if (modeloBloqueado) return duplicidad?.mensaje ?? "El modelo ya tiene una solicitud que impide registrar una nueva.";
    if (!comentario.trim()) return "El comentario es obligatorio.";
    if (isCU) {
      if (!esquema) return "Selecciona el esquema.";
      if (!tipoProducto) return "Selecciona el tipo de producto.";
      if (normas.length === 0) return "Agrega al menos una NOM/DT aplicable.";
      if (!bodCalle.trim() || !bodNumExt.trim() || !bodCP.trim() || !bodColonia || !bodMunicipio.trim() || !bodEntidad.trim()) {
        return "Completa el domicilio de la bodega.";
      }
      if (!bodNombreResp.trim() || !bodTelefonoResp.trim()) return "Captura responsable y teléfono de la bodega.";
      if (!TELEFONO_RX.test(bodTelefonoResp)) return "Teléfono de la bodega con formato inválido.";
    } else {
      if (!bajoProtesta) return "Debes marcar 'Bajo protesta de decir verdad'.";
    }
    return null;
  };

  const submit = async () => {
    // Si la solicitud ya se creó (reintento de subida), no revalides el form.
    if (!solicitudCreada) {
      const err = validar();
      if (err) { setError(err); return; }
    }
    setError(null);
    setEnviando(true);
    try {
      let id = solicitudCreada;
      if (!id) {
        const { data } = await api.post<{ id: string }>(
          "/api/sector/telecom/solicitudes",
          construirBody(),
        );
        id = data.id;
        setSolicitudCreada(id);
      }
      let fallidos = 0;
      if (docs.length) {
        // Los documentos del expediente se cargan y se VINCULAN al expediente
        // técnico, de modo que cuenten para la completitud (no quedan sólo en el
        // contexto de la solicitud). El expediente se crea bajo demanda.
        let expId = expedienteCreado;
        if (!expId) {
          const { data } = await api.post<{ id: string }>(
            "/api/sector/telecom/expedientes",
            { solicitudId: id },
          );
          expId = data.id;
          setExpedienteCreado(expId);
        }
        fallidos = await subirDocumentosCapturados(
          docs,
          setDocs,
          "EXPEDIENTE_TELECOM",
          expId,
          async (documentoId) => {
            await api.post(`/api/sector/telecom/expedientes/${expId}/documentos`, { documentoId });
          },
        );
      }
      if (fallidos > 0) {
        setError(
          `La solicitud se creó, pero ${fallidos} documento(s) no se pudieron subir. ` +
            `Reintenta los marcados en rojo o continúa y adjúntalos desde el expediente.`,
        );
        setStep(PASO_DOCS); // mostrar el paso de documentos con los fallidos
        setEnviando(false);
        return;
      }
      navigate(`/sector/telecom/solicitudes/${id}/success`);
    } catch (e: unknown) {
      const r = e as { response?: { data?: { detail?: string; title?: string; message?: string } } };
      setError(r.response?.data?.detail || r.response?.data?.message || r.response?.data?.title || (e as Error).message);
      setEnviando(false);
    }
  };

  // -- Validación por paso del wizard --
  const validarPaso = (p: number): string | null => {
    switch (p) {
      case 0:
        if (!titular) return "Selecciona un titular.";
        if (!tramitadorBloqueado && numTramitadoresAut > 1 && !tramitador)
          return "Selecciona el tramitador autorizado para este titular.";
        return null;
      case 1:
        if (!esquema) return "Selecciona el esquema.";
        if (!tipoProducto) return "Selecciona el tipo de producto.";
        return null;
      case 2:
        if (!productoNombre.trim() || !productoModelo.trim() || !marca.trim())
          return "Producto, modelo y marca son obligatorios.";
        // Duplicidad del modelo: no dejamos avanzar para no hacer capturar el
        // resto del formulario cuando el backend lo va a rechazar igual.
        if (modeloBloqueado) return duplicidad?.mensaje ?? "El modelo ya tiene una solicitud que impide registrar una nueva.";
        return null;
      case 3:
        if (!bodCalle.trim() || !bodNumExt.trim() || !bodCP.trim() || !bodColonia || !bodMunicipio.trim() || !bodEntidad.trim())
          return "Completa el domicilio de la bodega.";
        if (!bodNombreResp.trim() || !bodTelefonoResp.trim()) return "Captura responsable y teléfono de la bodega.";
        if (!TELEFONO_RX.test(bodTelefonoResp)) return "Teléfono de la bodega con formato inválido.";
        return null;
      case 4:
        return normas.length === 0 ? "Agrega al menos una NOM/DT aplicable." : null;
      default:
        return null;
    }
  };

  const esUltimo = step === PASO_TOTAL - 1;
  const irSiguiente = () => {
    const err = validarPaso(step);
    if (err) { setError(err); return; }
    setError(null);
    setStep((s) => Math.min(s + 1, PASO_TOTAL - 1));
  };
  const irAtras = () => { setError(null); setStep((s) => Math.max(s - 1, 0)); };
  const irA = (i: number) => { if (i <= step) { setError(null); setStep(i); } };

  // -- Render --
  return (
    <PageContainer width="wide">
      <PageHeader
        backTo="/telecom"
        backLabel="Volver a Telecom"
        title="Nueva solicitud Telecom"
        subtitle="Captura los datos para iniciar el trámite de Certificado de Conformidad Único. Si actúas vía tramitador externo, debe existir carta poder vigente."
      />

      <WizardProgress titulos={PASOS_TITULOS} actual={step} onJump={irA} />

      {/* Paso 0 — Solicitante */}
      {step === 0 && (
      <Section title="Datos del solicitante">
        {ctxActivo && !ctxEsTramitadorPuro && (
          <Alert variant="info" className="mb-3">
            Estás creando una nueva solicitud para <strong>{ctxActivo.titularRazonSocial}</strong>.
            <span className="mt-0.5 block text-xs">
              Si necesitas trabajar con otra empresa, vuelve a Telecom y cámbiala en el selector de empresa de la barra superior.
            </span>
          </Alert>
        )}
        {ctxEsTramitadorPuro && (
          <Alert variant="info" title={`Estás actuando como tramitador (${ctxActivo?.titularRazonSocial}).`} className="mb-3">
            Selecciona la empresa <strong>titular cliente</strong> para la cual estás levantando esta solicitud.
            Tu Registro Legal ya aparece como tramitador en el campo de abajo.
          </Alert>
        )}
        <div className="space-y-3">
          <Field label="Titular *">
            {titularBloqueado ? (
              <ParteBloqueada
                razonSocial={titular?.razonSocial ?? ctxActivo?.titularRazonSocial ?? "—"}
                nota="Definido por tu empresa; no editable."
              />
            ) : (
              <TitularPicker value={titular} onChange={setTitular} />
            )}
          </Field>
          {tramitadorBloqueado ? (
            <Field label="Tramitador">
              <ParteBloqueada
                razonSocial={
                  tramitador?.razonSocial ??
                  ctxActivo?.tramitadorRazonSocial ??
                  ctxActivo?.titularRazonSocial ??
                  "—"
                }
                nota="Tramitador externo definido por carta poder; no editable."
              />
            </Field>
          ) : (
            <TramitadorAutorizadoField
              titularId={titular?.id}
              value={tramitador}
              onChange={setTramitador}
              onAutorizadosChange={setNumTramitadoresAut}
            />
          )}
        </div>
      </Section>

      )}

      {/* Paso 1 — Servicio */}
      {step === 1 && (
      <Section title="Servicio solicitado">
        <Field label="Servicio *">
          <select value={servicio} onChange={(e) => cambiarServicio(e.target.value)} disabled
                  className="h-10 w-full rounded-md border border-edge-strong bg-surface px-3 text-sm shadow-sm transition placeholder:text-fg-faint focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:border-edge disabled:bg-surface-2 disabled:text-fg-subtle">
            {(qServicios.data?.filter((s) => s.codigo === CERTIFICADO_UNICO) ?? [{ codigo: CERTIFICADO_UNICO, nombre: "Certificado único", descripcion: "" }]).map((s) => (
              <option key={s.codigo} value={s.codigo} title={s.descripcion}>{s.nombre}</option>
            ))}
          </select>
          <p className="mt-1 text-xs text-fg-subtle">
            Los demás trámites (reexpedición, copia fiel, ampliaciones) se inician desde el{" "}
            <Link to="/telecom/servicios" className="text-primary-strong hover:underline">menú de servicios</Link>.
          </p>
        </Field>
        {servicio === OTRO && (
          <Field label="Otro (especificar) *">
            <input value={otroServicio} onChange={(e) => setOtroServicio(e.target.value)}
                   maxLength={150} className="h-10 w-full rounded-md border border-edge-strong bg-surface px-3 text-sm shadow-sm transition placeholder:text-fg-faint focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:border-edge disabled:bg-surface-2 disabled:text-fg-subtle" />
          </Field>
        )}
        {isCU && (
          <>
            <Field label="Esquema *">
              <div className="grid gap-2 sm:grid-cols-2">
                {qEsquemas.data?.map((e) => {
                  const sel = esquema === e.codigo;
                  return (
                    <label
                      key={e.codigo}
                      className={`flex cursor-pointer items-start gap-2 rounded border p-2.5 text-sm transition ${
                        sel
                          ? "border-primary-strong bg-primary-tint ring-1 ring-primary"
                          : "border-edge hover:border-edge-strong hover:bg-surface-2"
                      }`}
                    >
                      <input
                        type="radio"
                        name="esquema"
                        value={e.codigo}
                        checked={sel}
                        onChange={() => { setEsquema(e.codigo); setTipoProducto(""); }}
                        className="mt-0.5"
                      />
                      <div className="min-w-0">
                        <div className="font-medium text-fg">{e.nombre}</div>
                        {e.descripcion && (
                          <div className="mt-0.5 text-xs leading-snug text-fg-muted">{e.descripcion}</div>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            </Field>
            <Field label="Tipo de producto *">
              <select value={tipoProducto} onChange={(e) => setTipoProducto(e.target.value)} disabled={!esquema}
                      className="h-10 w-full rounded-md border border-edge-strong bg-surface px-3 text-sm shadow-sm transition placeholder:text-fg-faint focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:border-edge disabled:bg-surface-2 disabled:text-fg-subtle">
                <option value="">— Selecciona —</option>
                {qTiposProducto.data?.map((t) => (
                  <option key={t.codigo} value={t.codigo}>{t.nombre}</option>
                ))}
              </select>
            </Field>
          </>
        )}
        {servicio === MUESTREO && (
          <p className="text-xs text-fg-subtle">El servicio de muestreo siempre usa Esquema 1.</p>
        )}
      </Section>

      )}

      {/* Paso 2 — Producto */}
      {step === 2 && (
      <div className="space-y-4">
      <Section title="Datos del producto">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Nombre del producto *">
            <input value={productoNombre} onChange={(e) => setProductoNombre(e.target.value)} className="h-10 w-full rounded-md border border-edge-strong bg-surface px-3 text-sm shadow-sm transition placeholder:text-fg-faint focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:border-edge disabled:bg-surface-2 disabled:text-fg-subtle" />
          </Field>
          <Field label="Marca *">
            <input value={marca} onChange={(e) => setMarca(e.target.value)} className="h-10 w-full rounded-md border border-edge-strong bg-surface px-3 text-sm shadow-sm transition placeholder:text-fg-faint focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:border-edge disabled:bg-surface-2 disabled:text-fg-subtle" />
          </Field>
          <Field label="Modelo principal *">
            <input value={productoModelo} onChange={(e) => setProductoModelo(e.target.value)} className="h-10 w-full rounded-md border border-edge-strong bg-surface px-3 text-sm shadow-sm transition placeholder:text-fg-faint focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:border-edge disabled:bg-surface-2 disabled:text-fg-subtle" />
            {qDuplicidad.isFetching && (
              <p className="mt-1 flex items-center gap-1.5 text-xs text-fg-subtle">
                <Loader2 className="h-3 w-3 animate-spin" /> Verificando duplicidad del modelo…
              </p>
            )}
          </Field>

          {/* Resultado de la validación de duplicidad: rojo bloquea el avance,
              ámbar permite continuar recomendando evidencia técnica. */}
          {duplicidad && duplicidad.decision !== "PERMITIDO" && (
            <div className="sm:col-span-2">
              <Alert
                variant={modeloBloqueado ? "error" : "warning"}
                title={
                  duplicidad.decision === "BLOQUEADO_ACEPTADO"
                    ? "Este modelo ya cuenta con certificación"
                    : duplicidad.decision === "BLOQUEADO_EN_PROCESO"
                      ? "Este modelo tiene un trámite en proceso"
                      : "Este modelo tuvo una solicitud rechazada"
                }
              >
                <p>{duplicidad.mensaje}</p>
                {duplicidad.sugerirEvidencia && (
                  <p className="mt-1">
                    Adjunta en el paso de documentos la evidencia técnica que respalde la
                    corrección. Es opcional, pero deja constancia de por qué se vuelve a
                    solicitar.
                  </p>
                )}
                {duplicidad.coincidencias.some((c) => c.mismoTitular) && (
                  <ul className="mt-1.5 space-y-0.5">
                    {duplicidad.coincidencias
                      .filter((c) => c.mismoTitular)
                      .map((c) => (
                        <li key={c.folio} className="font-mono text-xs">
                          {c.folio} — {c.estadoNombre ?? c.estadoCodigo}
                        </li>
                      ))}
                  </ul>
                )}
              </Alert>
            </div>
          )}
          {isCU && (
            <Field label="Modelo(s) adicional(es)">
              <input value={productoModelos} onChange={(e) => setProductoModelos(e.target.value)}
                     placeholder="separados por coma" className="h-10 w-full rounded-md border border-edge-strong bg-surface px-3 text-sm shadow-sm transition placeholder:text-fg-faint focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:border-edge disabled:bg-surface-2 disabled:text-fg-subtle" />
            </Field>
          )}
          {isCU && (
            <>
              <Field label="Accesorios">
                <input value={productoAccesorios} onChange={(e) => setProductoAccesorios(e.target.value)} className="h-10 w-full rounded-md border border-edge-strong bg-surface px-3 text-sm shadow-sm transition placeholder:text-fg-faint focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:border-edge disabled:bg-surface-2 disabled:text-fg-subtle" />
              </Field>
              <Field label="Fabricante">
                <input value={productoFabricante} onChange={(e) => setProductoFabricante(e.target.value)} className="h-10 w-full rounded-md border border-edge-strong bg-surface px-3 text-sm shadow-sm transition placeholder:text-fg-faint focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:border-edge disabled:bg-surface-2 disabled:text-fg-subtle" />
              </Field>
              <Field label="Fracción(es) arancelaria(s)" span={2}>
                <input value={productoFraccion} onChange={(e) => setProductoFraccion(e.target.value.replace(/[^\d,\s]/g, ""))}
                       placeholder="solo dígitos, comas y espacios" className="h-10 w-full rounded-md border border-edge-strong bg-surface px-3 text-sm shadow-sm transition placeholder:text-fg-faint focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:border-edge disabled:bg-surface-2 disabled:text-fg-subtle" />
              </Field>
              {(esquema === "ESQUEMA_1" || esquema === "ESQUEMA_2") && (
                <Field label="Cantidad de productos por lote">
                  <input type="number" min={0} max={999999999} value={productoCantidadLote}
                         onChange={(e) => setProductoCantidadLote(e.target.value)} className="h-10 w-full rounded-md border border-edge-strong bg-surface px-3 text-sm shadow-sm transition placeholder:text-fg-faint focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:border-edge disabled:bg-surface-2 disabled:text-fg-subtle" />
                </Field>
              )}
              {esquema === "ESQUEMA_1" && (
                <Field label="Cantidad de números de serie">
                  <input type="number" min={0} max={999999999} value={productoCantidadSerie}
                         onChange={(e) => setProductoCantidadSerie(e.target.value)} className="h-10 w-full rounded-md border border-edge-strong bg-surface px-3 text-sm shadow-sm transition placeholder:text-fg-faint focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:border-edge disabled:bg-surface-2 disabled:text-fg-subtle" />
                </Field>
              )}
            </>
          )}
        </div>

        {isCU && (
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <PaisesPicker
              label="País(es) de origen *"
              opciones={qPaises.data ?? []}
              seleccion={paisesOrigen}
              onChange={setPaisesOrigen}
              required
            />
            <div className="space-y-2">
              <PaisesPicker
                label="País(es) de procedencia *"
                opciones={qPaises.data ?? []}
                seleccion={paisesProcedencia}
                onChange={setPaisesProcedencia}
                required
              />
              {paisesOrigen.length > 0 && !mismosPaises(paisesOrigen, paisesProcedencia) && (
                <button
                  type="button"
                  onClick={() => setPaisesProcedencia(paisesOrigen)}
                  className="inline-flex w-full items-center justify-center gap-1.5 rounded-md border-2 border-dashed border-primary-strong bg-primary-tint px-3 py-2 text-sm font-medium text-primary-strong transition hover:bg-primary/15 hover:border-solid"
                >
                  <Copy className="h-4 w-4" />
                  Usar los mismos países de origen
                  <span className="rounded-full bg-surface px-1.5 py-0.5 text-[10px] font-semibold">
                    {paisesOrigen.length}
                  </span>
                </button>
              )}
            </div>
          </div>
        )}
      </Section>

      {/* Datos adicionales — varía por servicio */}
      {servicio && (
        <Section title={isCU ? "Datos adicionales" : "Datos adicionales para certificación"}>
          {isCU ? (
            <Field label="No. de Familia(s) o Grupo(s)">
              <input value={numFamilia} onChange={(e) => setNumFamilia(e.target.value)} className="h-10 w-full rounded-md border border-edge-strong bg-surface px-3 text-sm shadow-sm transition placeholder:text-fg-faint focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:border-edge disabled:bg-surface-2 disabled:text-fg-subtle uppercase" />
            </Field>
          ) : (
            <>
              <Field label="No. de Certificado anterior">
                <input value={certificadoAnterior} onChange={(e) => setCertificadoAnterior(e.target.value)}
                       className="h-10 w-full rounded-md border border-edge-strong bg-surface px-3 text-sm shadow-sm transition placeholder:text-fg-faint focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:border-edge disabled:bg-surface-2 disabled:text-fg-subtle uppercase" />
              </Field>
              <label className="inline-flex items-start gap-2 text-sm">
                <input type="checkbox" checked={bajoProtesta} onChange={(e) => setBajoProtesta(e.target.checked)} className="mt-0.5" />
                <span>Declaro <strong>bajo protesta de decir verdad</strong> que la información proporcionada es verídica. *</span>
              </label>
            </>
          )}
        </Section>
      )}

      </div>
      )}

      {/* Paso 3 — Bodega */}
      {step === 3 && isCU && (
        <Section title="Datos de la bodega o punto de venta">
          <label className="mb-3 inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={bodMismoDom} onChange={(e) => setBodMismoDom(e.target.checked)} />
            La bodega tiene el mismo domicilio fiscal que el titular
          </label>
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Calle *">
              <input value={bodCalle} onChange={(e) => setBodCalle(e.target.value)} disabled={bodMismoDom}
                     className="h-10 w-full rounded-md border border-edge-strong bg-surface px-3 text-sm shadow-sm transition placeholder:text-fg-faint focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:border-edge disabled:bg-surface-2 disabled:text-fg-subtle uppercase" />
            </Field>
            <Field label="Núm. exterior *">
              <input value={bodNumExt} onChange={(e) => setBodNumExt(e.target.value)} disabled={bodMismoDom}
                     className="h-10 w-full rounded-md border border-edge-strong bg-surface px-3 text-sm shadow-sm transition placeholder:text-fg-faint focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:border-edge disabled:bg-surface-2 disabled:text-fg-subtle uppercase" />
            </Field>
            <Field label="Núm. interior">
              <input value={bodNumInt} onChange={(e) => setBodNumInt(e.target.value)} disabled={bodMismoDom}
                     className="h-10 w-full rounded-md border border-edge-strong bg-surface px-3 text-sm shadow-sm transition placeholder:text-fg-faint focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:border-edge disabled:bg-surface-2 disabled:text-fg-subtle uppercase" />
            </Field>
            <Field label="Código postal *">
              <input value={bodCP} maxLength={5} onChange={(e) => setBodCP(e.target.value.replace(/\D/g, ""))} disabled={bodMismoDom}
                     className="h-10 w-full rounded-md border border-edge-strong bg-surface px-3 text-sm shadow-sm transition placeholder:text-fg-faint focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:border-edge disabled:bg-surface-2 disabled:text-fg-subtle" />
            </Field>
            <Field label="Entidad federativa *">
              <input value={bodEntidad} readOnly disabled className="h-10 w-full rounded-md border border-edge-strong bg-surface px-3 text-sm shadow-sm transition placeholder:text-fg-faint focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:border-edge disabled:bg-surface-2 disabled:text-fg-subtle" />
            </Field>
            <Field label="Municipio *">
              <input value={bodMunicipio} readOnly disabled className="h-10 w-full rounded-md border border-edge-strong bg-surface px-3 text-sm shadow-sm transition placeholder:text-fg-faint focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:border-edge disabled:bg-surface-2 disabled:text-fg-subtle" />
            </Field>
            <Field label="Colonia *" span={3}>
              <select value={bodColonia} onChange={(e) => setBodColonia(e.target.value)} disabled={bodMismoDom || coloniasSepomex.length === 0}
                      className="h-10 w-full rounded-md border border-edge-strong bg-surface px-3 text-sm shadow-sm transition placeholder:text-fg-faint focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:border-edge disabled:bg-surface-2 disabled:text-fg-subtle">
                <option value="">— Selecciona —</option>
                {coloniasSepomex.map((c) => (<option key={c} value={c}>{c}</option>))}
              </select>
            </Field>
            <Field label="Nombre del responsable *" span={2}>
              <input value={bodNombreResp} onChange={(e) => setBodNombreResp(e.target.value)}
                     className="h-10 w-full rounded-md border border-edge-strong bg-surface px-3 text-sm shadow-sm transition placeholder:text-fg-faint focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:border-edge disabled:bg-surface-2 disabled:text-fg-subtle" />
            </Field>
            <Field label="Teléfono *">
              <input value={bodTelefonoResp} onChange={(e) => setBodTelefonoResp(e.target.value)}
                     placeholder="(55) 12-34-56-78" className="h-10 w-full rounded-md border border-edge-strong bg-surface px-3 text-sm shadow-sm transition placeholder:text-fg-faint focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:border-edge disabled:bg-surface-2 disabled:text-fg-subtle" />
            </Field>
          </div>
        </Section>
      )}

      {/* Paso 4 — Normas */}
      {step === 4 && isCU && (
        <Section title={`NOM / DT aplicable${normas.length > 0 ? ` (${normas.length})` : ""}`}>
          <Button
            size="sm"
            variant="secondary"
            className="mb-3"
            icon={<Plus className="h-4 w-4" />}
            onClick={() => setModalNorma(nuevaNormaForm({ modelosEvaluados: productoModelo }))}
          >
            Agregar norma
          </Button>
          {normas.length === 0 ? (
            <div className="rounded-lg border border-dashed border-edge-strong p-3 text-sm text-fg-subtle">
              Aún no agregas normas/DT aplicables.
            </div>
          ) : (
            <div className="w-full overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-2 text-xs uppercase text-fg-subtle">
                <tr>
                  <th className="px-3 py-2.5 text-left">Norma</th>
                  <th className="px-3 py-2.5 text-left">Laboratorio</th>
                  <th className="px-3 py-2.5 text-left">Fecha emisión</th>
                  <th className="w-24 px-3 py-2.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-edge">
                {normas.map((n, i) => (
                  <tr key={n.uiId}>
                    <td className="px-3 py-2.5 font-mono text-xs">{n.normaCodigo}</td>
                    <td className="px-3 py-2.5 text-xs">{n.esNom221P1 ? `${n.numImeis ?? 0} IMEIs` : (n.laboratorioNombre || "—")}</td>
                    <td className="px-3 py-2.5 text-xs">{n.fechaEmision || "—"}</td>
                    <td className="px-3 py-2.5 text-right">
                      <button type="button" onClick={() => setModalNorma(n)} className="rounded p-1 text-fg-muted transition hover:bg-surface-2"><Pencil className="h-4 w-4" /></button>
                      <button type="button" onClick={() => setNormas((arr) => arr.filter((_, idx) => idx !== i))} className="rounded p-1 text-estado-err transition hover:bg-estado-err-bg"><Trash2 className="h-4 w-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </Section>
      )}

      {/* Paso 5 — Revisar y enviar */}
      {step === 5 && (
      <div className="space-y-4">
      <ResumenSolicitud
        titular={titular?.razonSocial ?? "—"}
        esquema={esquema || "—"}
        producto={`${productoNombre} ${productoModelo}`.trim() || "—"}
        marca={marca || "—"}
        numNormas={normas.length}
        numDocs={docs.length}
      />
      <Section title="Comentarios">
        <Textarea value={comentario} onChange={(e) => setComentario(e.target.value.slice(0, 450))}
                  rows={5} maxLength={450} placeholder="Captura cualquier detalle adicional para el equipo de Analistas (obligatorio)" />
        <div className="mt-1 text-right text-xs text-fg-subtle">{comentario.length}/450</div>
      </Section>

      {/* Documentos del expediente — parte de la captura (solo Certificado Único) */}
      {isCU && (
        <CapturaDocumentos
          tag="TELECOM"
          titulo="Documentos del expediente"
          ayuda="Adjunta los documentos del expediente técnico. Todos son opcionales: puedes enviar la solicitud y completarlos después desde el expediente."
          docs={docs}
          onChange={setDocs}
          disabled={enviando}
        />
      )}
      </div>
      )}

      {error && <Alert variant="error">{error}</Alert>}

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
        <Button variant="secondary" disabled={enviando} onClick={() => navigate("/telecom")}>Cancelar</Button>
        <div className="flex flex-col-reverse gap-2 sm:flex-row">
          {step > 0 && !solicitudCreada && (
            <Button variant="secondary" disabled={enviando} icon={<ChevronLeft className="h-4 w-4" />} onClick={irAtras}>
              Atrás
            </Button>
          )}
          {solicitudCreada ? (
            <>
              <Button variant="secondary" loading={enviando} onClick={submit}>
                Reintentar documentos
              </Button>
              <Button variant="success" onClick={() => navigate(`/sector/telecom/solicitudes/${solicitudCreada}/success`)}>
                Continuar
              </Button>
            </>
          ) : esUltimo ? (
            <Button loading={enviando} icon={<Save className="h-4 w-4" />} onClick={submit}>
              {enviando ? "Enviando…" : "Crear solicitud"}
            </Button>
          ) : (
            <Button onClick={irSiguiente}>
              Siguiente <ChevronRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {modalNorma && (
        <NormaModal
          initial={modalNorma}
          normas={qNormas.data ?? []}
          yaUsadas={normas.filter((n) => n.uiId !== modalNorma.uiId).map((n) => n.normaId)}
          onCancel={() => setModalNorma(null)}
          onSave={(n) => {
            setNormas((arr) => {
              const i = arr.findIndex((x) => x.uiId === n.uiId);
              if (i >= 0) { const next = [...arr]; next[i] = n; return next; }
              return [...arr, n];
            });
            setModalNorma(null);
          }}
        />
      )}
    </PageContainer>
  );
}

// ============================================================================
// Subcomponentes
// ============================================================================

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card title={title} bodyClassName="space-y-3">
      {children}
    </Card>
  );
}

/** Barra de progreso del wizard: stepper en escritorio, progreso compacto en móvil. */
function WizardProgress({
  titulos,
  actual,
  onJump,
}: {
  titulos: string[];
  actual: number;
  onJump: (i: number) => void;
}) {
  const total = titulos.length;
  return (
    <Card padding="sm">
      {/* Móvil: progreso compacto */}
      <div className="sm:hidden">
        <div className="flex items-center justify-between text-xs">
          <span className="font-semibold text-primary-strong">Paso {actual + 1} de {total}</span>
          <span className="text-fg-muted">{titulos[actual]}</span>
        </div>
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-surface-2">
          <div className="h-full rounded-full bg-primary transition-all duration-300" style={{ width: `${((actual + 1) / total) * 100}%` }} />
        </div>
      </div>
      {/* Escritorio: stepper con saltos a pasos anteriores */}
      <ol className="hidden items-center sm:flex">
        {titulos.map((t, i) => {
          const done = i < actual;
          const current = i === actual;
          return (
            <li key={t} className="flex flex-1 items-center last:flex-none">
              <button
                type="button"
                onClick={() => onJump(i)}
                disabled={i > actual}
                className={`flex items-center gap-2 ${i <= actual ? "cursor-pointer" : "cursor-default"}`}
              >
                <span
                  className={`grid h-8 w-8 shrink-0 place-items-center rounded-full text-xs font-semibold transition ${
                    current
                      ? "bg-primary text-white ring-4 ring-primary/15"
                      : done
                        ? "bg-estado-ok text-white"
                        : "bg-surface-2 text-fg-faint"
                  }`}
                >
                  {done ? <Check className="h-4 w-4" /> : i + 1}
                </span>
                <span className={`whitespace-nowrap text-xs ${current ? "font-semibold text-fg" : done ? "text-fg-muted" : "text-fg-faint"}`}>
                  {t}
                </span>
              </button>
              {i < total - 1 && <span className={`mx-3 h-0.5 flex-1 rounded-full transition ${done ? "bg-estado-ok/50" : "bg-edge"}`} />}
            </li>
          );
        })}
      </ol>
    </Card>
  );
}

/** Resumen compacto de la captura, mostrado en el paso final. */
function ResumenSolicitud({
  titular,
  esquema,
  producto,
  marca,
  numNormas,
  numDocs,
}: {
  titular: string;
  esquema: string;
  producto: string;
  marca: string;
  numNormas: number;
  numDocs: number;
}) {
  return (
    <Card title="Resumen">
      <dl className="grid gap-4 sm:grid-cols-2">
        <DataField label="Titular" value={titular} />
        <DataField label="Esquema" value={esquema} />
        <DataField label="Producto" value={producto} />
        <DataField label="Marca" value={marca} />
        <DataField label="NOM/DT aplicables" value={String(numNormas)} />
        <DataField label="Documentos adjuntos" value={String(numDocs)} />
      </dl>
    </Card>
  );
}

function mismosPaises(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sa = new Set(a);
  return b.every((id) => sa.has(id));
}

function Field({ label, children, span = 1 }: { label: string; children: React.ReactNode; span?: 1 | 2 | 3 }) {
  const cls = span === 3 ? "sm:col-span-3" : span === 2 ? "sm:col-span-2" : undefined;
  return (
    <FormField label={label} className={cls}>
      {children}
    </FormField>
  );
}

// ============================================================================
// Utils
// ============================================================================

function extractImeisDeNormas(normas: NormaForm[]): number | null {
  const nom221 = normas.find((n) => n.esNom221P1);
  return nom221?.numImeis ?? null;
}
